import type { ButtonProps as ChakraButtonProps } from '@chakra-ui/react'
import {
  AbsoluteCenter,
  Box,
  Button as ChakraButton,
  Span,
  Spinner,
} from '@chakra-ui/react'
import * as React from 'react'

interface ButtonLoadingProps {
  loading?: boolean
  loadingText?: React.ReactNode
}

export interface ButtonProps extends ChakraButtonProps, ButtonLoadingProps {
  variant?: 'solid' | 'outline' | 'ghost'
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      loading,
      disabled,
      loadingText,
      children,
      size,
      leftIcon,
      rightIcon,
      variant = 'solid',
      colorPalette,
      ...rest
    } = props

    const hasIcon = !!leftIcon || !!rightIcon
    let iconSize: string | undefined = undefined
    let paddingInline = 3
    let gap = 2
    let height: string | undefined = undefined
    switch (size) {
      case '2xs':
      case 'xs':
        iconSize = hasIcon ? '14px' : undefined
        paddingInline = 2
        gap = 1
        height = '24px'
        break
      case 'sm':
        iconSize = hasIcon ? '16px' : undefined
        paddingInline = 3
        gap = 2
        height = '32px'
        break
      case 'md':
        iconSize = hasIcon ? '20px' : undefined
        paddingInline = 4
        gap = 2
        height = '40px'
        break
      case 'lg':
        iconSize = hasIcon ? '20px' : undefined
        paddingInline = 6
        gap = 2
        height = '48px'
        break
      case 'xl':
      case '2xl':
        iconSize = hasIcon ? '20px' : undefined
        break
      default:
        iconSize = hasIcon ? '20px' : undefined
    }

    let color: string | undefined = undefined
    let bgColor: string = 'colorPalette.500'
    let borderColor: string | undefined = undefined
    let hoverBgColor: string = 'colorPalette.500/90'
    let hoverBg =
      'var(--hover-bg-color) radial-gradient(circle, transparent 1%, var(--hover-bg-color) 1%) center/12500%'
    let activeBgColor: string = 'colorPalette.500/80'
    switch (variant) {
      case 'solid':
        if (colorPalette === 'gray') {
          color = 'colorPalette.700'
          bgColor = 'colorPalette.100'
          hoverBgColor = 'colorPalette.700/8'
          hoverBg =
            'var(--hover-bg-color) radial-gradient(circle, var(--hover-bg-color) 1%, transparent 1%) center/15000%'
          activeBgColor = 'colorPalette.700/16'
        }
        break
      case 'outline':
        color =
          colorPalette === 'gray' ? 'colorPalette.700' : 'colorPalette.500'
        bgColor = 'transparent'
        borderColor =
          colorPalette === 'gray' ? 'colorPalette.200' : 'colorPalette.500'
        hoverBgColor = 'colorPalette.500/8'
        hoverBg =
          'var(--hover-bg-color) radial-gradient(circle, var(--hover-bg-color) 1%, transparent 1%) center/15000%'
        activeBgColor = 'colorPalette.500/16'
        break
      case 'ghost':
        color =
          colorPalette === 'gray' ? 'colorPalette.700' : 'colorPalette.500'
        bgColor = 'transparent'
        hoverBgColor = 'colorPalette.500/8'
        hoverBg =
          'var(--hover-bg-color) radial-gradient(circle, var(--hover-bg-color) 1%, transparent 1%) center/15000%'
        activeBgColor = 'colorPalette.500/16'
        break
    }

    return (
      <ChakraButton
        colorPalette={colorPalette}
        variant={variant}
        disabled={loading || disabled}
        ref={ref}
        size={size}
        color={color}
        borderRadius="base"
        borderColor={borderColor}
        paddingInline={paddingInline}
        gap={gap}
        h={height}
        {...rest}
        // NOTE: クリック時のエフェクトを追加する
        // _hover,_active prop でCSSを指定するとセレクタが複雑になりエフェクトが上手く描画されないため css prop で直接指定する
        bgColor={bgColor}
        bgPos="center"
        transition="background 0.8s"
        css={{
          '--hover-bg-color': `{colors.${hoverBgColor}}`,
          '&:hover': {
            background: hoverBg,
          },
          '&:active': {
            backgroundColor: activeBgColor,
            backgroundSize: '100%',
            transition: 'background 0s',
          },
        }}
      >
        {loading && !loadingText ? (
          <>
            <AbsoluteCenter display="inline-flex">
              <Spinner size="inherit" color="inherit" />
            </AbsoluteCenter>
            <Span opacity={0}>{children}</Span>
          </>
        ) : loading && loadingText ? (
          <>
            <Spinner size="inherit" color="inherit" />
            {loadingText}
          </>
        ) : (
          <>
            {leftIcon && (
              <Box w={iconSize} h={iconSize}>
                {leftIcon}
              </Box>
            )}
            {children}
            {rightIcon && (
              <Box w={iconSize} h={iconSize}>
                {rightIcon}
              </Box>
            )}
          </>
        )}
      </ChakraButton>
    )
  },
)
